import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';

// Temporary development gate. Client-side only: it keeps casual visitors out
// while the app is unfinished and is not a security boundary. Remove this
// component (and its wrapper in App.tsx) once the app is ready for users.
const DEV_PASSWORD = 'test321';
const STORAGE_KEY = 'havu-dev-unlocked';

const readUnlocked = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === DEV_PASSWORD;
  } catch {
    return false;
  }
};

interface DevPasswordGateProps {
  children: ReactNode;
}

function DevPasswordGate({ children }: DevPasswordGateProps) {
  const [unlocked, setUnlocked] = useState(readUnlocked);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // React 18 does not forward `inert`, so set it directly to keep keyboard
  // focus out of the blurred app behind the gate.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    if (unlocked) {
      content.removeAttribute('inert');
    } else {
      content.setAttribute('inert', '');
    }
  }, [unlocked]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (password !== DEV_PASSWORD) {
      setError(true);
      return;
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, DEV_PASSWORD);
    } catch {
      // Storage unavailable: stay unlocked for this session only.
    }
    setUnlocked(true);
  };

  return (
    <>
      <div
        ref={contentRef}
        className={unlocked ? undefined : 'dev-gate-content'}
        aria-hidden={unlocked ? undefined : true}
      >
        {children}
      </div>
      {!unlocked && (
        <div className="dev-gate-overlay">
          <form
            className="dev-gate-card"
            onSubmit={handleSubmit}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dev-gate-title"
          >
            <h2 id="dev-gate-title">Under development</h2>
            <p>This app is not ready for use yet. Enter the access password to continue.</p>
            <input
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setError(false);
              }}
              placeholder="Password"
              aria-label="Password"
              aria-invalid={error}
              autoFocus
            />
            {error && <p className="dev-gate-error">Incorrect password.</p>}
            <button type="submit" className="primary-button">
              Continue
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default DevPasswordGate;
